import { createPortal } from 'react-dom'
import { Button } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { Preppy } from './Preppy'
import { useAssistant } from './AssistantProvider'
import { isWelcomePopup } from './types'
import { DOCK_SIZE } from './PreppyDock'
import { ASSISTANT_PANEL_WIDTH } from './AssistantPanel'

/**
 * A message from Preppy, in the corner.
 *
 * Every trigger pops here as it fires; the Pop-ups tab is the history. The
 * bubble sits just left of the dock with its tail pointing at Preppy's face,
 * and follows the dock when you drag it — a speech bubble pointing at nothing
 * is just a notification.
 *
 * It pops with the panel open too, stepping left of it rather than under it.
 * The dock is hidden then, so the tail points at the panel — which is where
 * Preppy has gone.
 */

const BUBBLE_WIDTH = 300
/** Roughly the tallest a bubble gets with buttons, used to keep it on screen. */
const BUBBLE_HEIGHT = 190

export function PopupBubble() {
  const { state, actions } = useAssistant()
  const { activePopup, supportNotice, tourSeen, dockTop, walk, open } = state

  if (walk) return null

  // A server pop-up goes first; the support notice waits for the corner to clear.
  const bubble = activePopup
    ? {
        label: isWelcomePopup(activePopup) ? 'First time here' : 'Pop-up',
        title: activePopup.title,
        body: activePopup.body,
        onOpen: actions.openActivePopup,
        onDismiss: actions.dismissPopup,
      }
    : supportNotice
      ? {
          label: 'Support',
          title: supportNotice.title,
          body: supportNotice.body,
          onOpen: actions.openSupportNotice,
          onDismiss: actions.dismissSupportNotice,
        }
      : null
  if (!bubble) return null

  const isWelcome = activePopup ? isWelcomePopup(activePopup) : false
  const showTourActions = isWelcome && !tourSeen
  // Follows the dock, clamped so a bubble opened near the bottom edge does not
  // hang off the screen.
  const top = Math.max(12, Math.min(window.innerHeight - BUBBLE_HEIGHT - 12, dockTop - 6))
  // Clamped the same way: stepping clear of the panel needs 748px of viewport,
  // and below that the bubble would sit off the left edge instead.
  const right = Math.min(
    open ? ASSISTANT_PANEL_WIDTH + 16 : DOCK_SIZE + 28,
    Math.max(0, window.innerWidth - BUBBLE_WIDTH - 12)
  )

  return createPortal(
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd>
      <div
        role="status"
        aria-live="polite"
        // Above the panel (z-50), not below it: under 748px the clamp pushes the
        // bubble over the panel, and a bubble hidden behind it would still be
        // holding the queue with no way for anyone to dismiss it.
        className="fixed z-[60] rounded-2xl rounded-tr-sm border border-gray-200 bg-white p-3.5 shadow-2xl transition-[right] duration-300 ease-in-out"
        style={{ width: BUBBLE_WIDTH, top, right }}
      >
        {/* tail, pointing at the dock */}
        <span
          aria-hidden
          className="absolute right-[-7px] top-5 h-3 w-3 rotate-45 border-r border-t border-gray-200 bg-white"
        />

        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label="Dismiss"
          onClick={bubble.onDismiss}
          className="absolute right-1.5 top-1.5 text-gray-300 hover:text-gray-600"
        />

        <button
          type="button"
          onClick={bubble.onOpen}
          className="flex w-full cursor-pointer items-start gap-2.5 border-0 bg-transparent p-0 text-left"
        >
          {isWelcome && (
            <span className="shrink-0">
              <Preppy mood={state.mood} size={36} />
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span
              className="block text-[9.5px] font-extrabold uppercase tracking-[0.11em]"
              style={{ color: 'var(--feature-primary-strong)' }}
            >
              {bubble.label}
            </span>
            <span className="mt-1 block pr-5 text-[13px] font-semibold leading-snug text-gray-900">
              {bubble.title}
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-gray-600">{bubble.body}</span>
          </span>
        </button>

        {showTourActions && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Button size="small" type="primary" className="feature-button-solid" onClick={actions.takeTour}>
              Show me around
            </Button>
            <Button size="small" onClick={actions.declineTour}>
              I will explore
            </Button>
          </div>
        )}
      </div>
    </FeatureThemeProvider>,
    document.body
  )
}
